<div
  class="bubble"
  data-testid="conversation-message"
  [attr.data-message-kind-value]="messageKind()"
  [ngClass]="{
    'is-unread': isUnread(),
    'is-typing': isTyping(),
  }"
  [class]="'position-' + bubblePosition()">
  <div class="content" [class]="'type-' + messageKind().toLowerCase()">
    @let timestamp = time();
    @if (!isTyping() && timestamp) {
      <div class="message-indicators">
        <shared-message-time [time]="timestamp" />
        @if (ownerOfMessage() && !messageHasError()) {
          <shared-message-state-indicator [state]="messageState()" />
        }
      </div>
    }

    @if (debug) {
      {{ messageSequence() }}
    }

    @let creator = showCreator();
    @if (creator) {
      <shared-message-sender data-testid="message-sender" [sender]="creator" />
    }

    @let text = messageText();
    @switch (messageKind()) {
      @case (messageKindEnum.Text) {
        @if (text) {
          <shared-message-text [messageText]="text" />
        }

        @let messageButtons = buttons();
        @if (messageButtons) {
          @for (button of messageButtons; track $index) {
            <orbit-button
              [label]="button.label"
              [icon]="button.icon"
              size="extra-small"
              boldLabel
              (onClick)="buttonClick.emit({ type: button.type })" />
          }
        }
      }

      @case (messageKindEnum.Location) {
        @let address = formattedAddress();
        @if (address) {
          <shared-message-text
            [messageText]="address"
            [link]="true"
            data-testid="message-location-text"
            (click)="
              buttonClick.emit({ type: messageButtonType.OpenGoogleMaps, data: coordinates })
            " />
        }

        @let coordinates = mapCenterCoordinates();
        @if (coordinates && coordinates.latitude && coordinates.longitude) {
          <shared-message-map [centerCoordinates]="coordinates" />
          <div class="message-map-addon">
            <div class="message-map-coordinates" data-testid="message-location-coordinates">
              {{ coordinates.latitude | number: '1.6-6' }},
              {{ coordinates.longitude | number: '1.6-6' }}
              <orbit-button
                [icon]="copyIcon"
                size="extra-small"
                [severity]="bubblePosition() === 'right' ? 'secondary' : null"
                (onClick)="
                  buttonClick.emit({ type: messageButtonType.CopyCoordinates, data: coordinates })
                " />
            </div>
            <div class="message-map-accuracy">
              {{ ('SHARED.CHAT_BUBBLE.MAP.ACCURACY' | translate) + ':' }}
              @if (coordinates.accuracy! < 30) {
                <span class="accuracy-good" data-testid="message-location-accuracy">
                  {{ 'SHARED.CHAT_BUBBLE.MAP.ACCURACY_LEVELS.HIGH' | translate }}
                </span>
              } @else if (coordinates.accuracy! < 100) {
                <span class="accuracy-medium" data-testid="message-location-accuracy">
                  {{ 'SHARED.CHAT_BUBBLE.MAP.ACCURACY_LEVELS.MEDIUM' | translate }}
                </span>
              } @else {
                <span class="accuracy-low" data-testid="message-location-accuracy">
                  {{ 'SHARED.CHAT_BUBBLE.MAP.ACCURACY_LEVELS.LOW' | translate }}
                </span>
              }
            </div>
          </div>
        }
      }

      @case (messageKindEnum.Image) {
        @if (text) {
          <shared-message-text data-testid="message-image-text" [messageText]="text" />
        }

        @let messageAsset = asset();
        @let messageAssetStatus = messageAsset?.status;
        @let isImageMissing =
          messageAssetStatus === 'UPLOAD_FAILED' ||
          messageAssetStatus === 'UNKNOWN' ||
          messageAssetStatus === 'UPLOAD_COMPLETED_MISSING';
        <div
          class="message-image-wrapper"
          [ngClass]="{
            'no-image': !messageAsset?.thumbnailUrl && !messageAsset?.downloadUrl,
            'image-loading': messageAssetStatus === 'UPLOAD_PENDING',
            'image-missing': isImageMissing,
          }">
          @if (isImageMissing) {
            <orbit-fa-icon class="image-icon" [iconName]="missingImageIcon" iconSize="2x" />
          } @else if (messageAssetStatus === 'UPLOAD_PENDING') {
            <div class="image-loading-wrapper">
              <orbit-fa-icon
                class="image-icon"
                [iconName]="loadingSpinnerIcon"
                animation="spin"
                iconSize="2x" />
            </div>
            <!-- ONLY TEMPORARY -->
            <!--            @if (messageAsset?.downloadUrl) {-->
            <!--              <img-->
            <!--                data-testid="message-image"-->
            <!--                [src]="messageAsset?.downloadUrl"-->
            <!--                alt="image"-->
            <!--                class="message-image" />-->
            <!--            }-->
          } @else if (messageAssetStatus === 'UPLOAD_COMPLETED') {
            <!--            @if (messageAsset?.thumbnailUrl) {-->
            <!--              <img-->
            <!--                data-testid="message-image"-->
            <!--                [src]="messageAsset?.thumbnailUrl"-->
            <!--                alt="image"-->
            <!--                class="message-image"-->
            <!--                role="button"-->
            <!--                tabindex="-1"-->
            <!--                (click)="assetClick.emit(messageId())" />-->
            <!--            } @else-->
            @if (messageAsset?.downloadUrl) {
              <img
                data-testid="message-image"
                [src]="messageAsset?.downloadUrl"
                alt="image"
                class="message-image"
                role="button"
                tabindex="-1"
                (click)="assetClick.emit(messageId())" />
            } @else {
              <orbit-fa-icon class="image-icon" [iconName]="imageIcon" />
            }
          }
        </div>
      }

      @case (messageKindEnum.Audio) {
        @if (text) {
          <shared-message-text [messageText]="text" data-testid="message-audio-text" />
        }
        @let messageAsset = asset();
        <div class="message-audio-wrapper" [ngClass]="{ 'no-audio': !messageAsset?.downloadUrl }">
          @if (messageAsset?.downloadUrl) {
            <audio controls [src]="messageAsset?.downloadUrl" data-testid="message-audio"></audio>
          } @else {
            <orbit-fa-icon class="audio-icon" [iconName]="audioIcon" />
          }
        </div>
      }

      @case (messageKindEnum.Video) {
        @if (text) {
          <shared-message-text [messageText]="text" data-testid="message-video-text" />
        }
        @let messageAsset = asset();
        <div class="message-video-wrapper" [ngClass]="{ 'no-video': !messageAsset?.downloadUrl }">
          @if (messageAsset?.downloadUrl) {
            @let iosVersion = windowService()?.iosVersion();
            @if (iosVersion && iosVersion < 16) {
              <div
                class="ios-video-icon"
                data-testid="message-video"
                [ngClass]="{ 'has-thumbnail': messageAsset?.thumbnailUrl }"
                [ngStyle]="{ 'background-image': thumbnailUrl() }">
                <orbit-fa-icon
                  class="video-icon"
                  [iconName]="videoIcon"
                  (click)="assetClick.emit(messageId())" />
                <orbit-fa-icon
                  class="video-open-icon"
                  [iconName]="videoOpenIcon"
                  (click)="assetClick.emit(messageId())" />
              </div>
            } @else {
              <video
                controls
                playsinline
                preload="none"
                data-testid="message-video"
                [poster]="messageAsset?.thumbnailUrl">
                <source [src]="messageAsset?.downloadUrl" type="video/mp4" />
              </video>
            }
          } @else {
            <orbit-fa-icon class="video-icon" [iconName]="videoIcon" />
          }
        </div>
      }

      @case (messageKindEnum.RequestLocation) {
        @if (text) {
          <shared-message-text [messageText]="text" data-testid="message-location-text" />
        }
        <orbit-button
          [label]="'SHARED.CONSTANTS.MESSAGE.LOCATION_REQUEST.BUTTON_LABEL' | translate"
          [icon]="locationIcon"
          [testId]="'btn-message-request-location'"
          size="small"
          [severity]="bubblePosition() === 'right' ? 'secondary' : null"
          (onClick)="buttonClick.emit({ type: MessageButtonType.LocationRequest })" />
      }
    }
    @if (isTyping()) {
      <orbit-fa-icon class="typing-icon" [iconName]="typingIcon" [animation]="'fade'" />
    }
  </div>
  <svg class="tail" xmlns="http://www.w3.org/2000/svg" width="9" height="8">
    <path
      d="M3.96.086c1.488 5.838 5.148 6.602 4.343 7.088-2.7.415-5.157-.076-7.345-.98 0 0 .288-.107.92-.819.634-.712.615-.741.84-1.101.417-.668.654-1.312.834-1.781C3.96 1.43 3.96.086 3.96.086Z" />
  </svg>
</div>
@if (messageHasError()) {
  <div class="error-icon-wrapper" data-testid="conversation-message-error">
    <orbit-fa-icon class="error-icon" [iconName]="errorIcon" />
  </div>

  <orbit-button
    [icon]="refreshIcon"
    size="extra-small"
    rounded
    (onClick)="resendMessage.emit(messageId())" />
}
